import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Share, PlusSquare, Smartphone, X } from 'lucide-react';

/**
 * InstallPOSPrompt
 *
 * Renders a small floating "Install POS" pill on the cashier login
 * page when the browser supports being added to the home screen, AND
 * when we are NOT already running as a standalone PWA. Tapping it
 * opens a dialog with the correct 1-2-3 instructions for the device:
 *
 *   - iPad/iPhone Safari → Share → Add to Home Screen   (manual)
 *   - Android Chrome     → fires the native install prompt
 *   - Desktop Chrome     → fires the native install prompt
 *
 * Hidden when the user is already inside the standalone POS app
 * (`display-mode: standalone` matches OR Safari's
 * `navigator.standalone` is true).
 */
export default function InstallPOSPrompt() {
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  // Soft-dismiss persisted per browser session. Init from storage so
  // we never need an effect→setState cycle (which the strict hooks
  // lint rule rejects).
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('pos_install_dismissed') === '1'; }
    catch { return false; }
  });

  // Detect environment once.
  const env = useMemo(() => {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isIPadOS13Up = /Mac/.test(ua) && typeof navigator?.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
    const isAndroid = /Android/.test(ua);
    const isStandalone =
      (typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(display-mode: standalone)').matches) ||
      (typeof navigator !== 'undefined' && navigator.standalone === true);
    return {
      isIOS: isIOS || isIPadOS13Up,
      isAndroid,
      isStandalone,
    };
  }, []);

  // Capture Chrome/Edge/Android beforeinstallprompt so we can fire it
  // on tap — much smoother UX than asking the user to dig into menus.
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (env.isStandalone || dismissed) return null;

  const triggerInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
      } catch { /* user cancelled */ }
      setDeferredPrompt(null);
      return;
    }
    // iOS Safari has no programmatic install — open the explainer.
    setOpen(true);
  };

  const handleDismiss = () => {
    sessionStorage.setItem('pos_install_dismissed', '1');
    setDismissed(true);
  };

  return (
    <>
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-white shadow-xl border rounded-full pl-1 pr-1 py-1" data-testid="install-pos-pill">
        <Button
          type="button"
          onClick={triggerInstall}
          className="rounded-full bg-orange-500 hover:bg-orange-600 text-white text-xs px-4 h-9"
          data-testid="install-pos-btn"
        >
          <Download size={14} className="mr-1.5" />
          Install POS to Home Screen
        </Button>
        <button
          type="button"
          onClick={handleDismiss}
          className="h-9 w-9 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
          aria-label="Dismiss install prompt"
          data-testid="install-pos-dismiss"
        >
          <X size={14} />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="install-pos-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone size={18} className="text-orange-500" />
              Install POS on your iPad / iPhone
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Apple devices don&apos;t show an auto-install button. Follow these 3 steps
            in Safari (Chrome on iPad won&apos;t work — it must be Safari):
          </p>
          <ol className="space-y-3 mt-3">
            <li className="flex items-start gap-3">
              <span className="shrink-0 h-7 w-7 rounded-full bg-orange-100 text-orange-700 font-bold text-sm flex items-center justify-center">1</span>
              <span className="text-sm flex-1">
                Tap the <strong>Share</strong> button
                <Share size={14} className="inline mx-1 -mt-0.5 text-info" />
                in Safari&apos;s toolbar (top-right on iPad, bottom on iPhone).
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="shrink-0 h-7 w-7 rounded-full bg-orange-100 text-orange-700 font-bold text-sm flex items-center justify-center">2</span>
              <span className="text-sm flex-1">
                Scroll down and tap <strong>Add to Home Screen</strong>
                <PlusSquare size={14} className="inline mx-1 -mt-0.5 text-info" />.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="shrink-0 h-7 w-7 rounded-full bg-orange-100 text-orange-700 font-bold text-sm flex items-center justify-center">3</span>
              <span className="text-sm flex-1">
                Keep the name <strong>POS</strong> and tap <strong>Add</strong>. You&apos;ll see an orange POS icon on your home screen — tapping it goes straight to the cashier PIN keypad, no browser bar.
              </span>
            </li>
          </ol>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-800 mt-3">
            <strong>Tip:</strong> If you don&apos;t see {'"Add to Home Screen"'}, make sure
            you opened this page in <strong>Safari</strong>, not in another app&apos;s
            in-app browser (WhatsApp / Instagram / etc.). Open the URL fresh in
            Safari and try again.
          </div>
          <div className="flex justify-end mt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-lg">Got it</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
